import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TablePagination,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import React, { useContext, useEffect, useState } from 'react';
import axios, { AxiosResponse, isAxiosError } from 'axios';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import { ClearIcon } from '@mui/x-date-pickers';
import VerifiedIcon from '@mui/icons-material/Verified';
import EditIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Preview';
import { emptyDriver } from '../../drivers/driver-forms/driver-form.utils';
import {
  ActorFilterOption,
  DeliveryStateFilterOption,
  IFilterOption,
  PaymentStateFilterOption,
} from '../../../common/filter-bar/types';
import { DoubleDate } from '../../../common/double-date-picker/double-date-picker.interfaces';
import { PaginationContext } from '../../../../contexts/pagination-context';
import { buildQueryParams } from '../../../common/filter-bar/filter-bar.utils';
import GenericTable from '../../../common/generic-table/generic-table';
import { displayMoney } from '../../../../utils/money';
import ConfirmAction from '../../../common/confirm-action/confirm-action';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';

import RestorePageIcon from '@mui/icons-material/RestorePage';
import CreatePaymentTask from '../../../common/create-payment-task/create-payment-task';
import InvoicePDFDialog from '../../../common/invoice-pdf-dialog/invoice-pdf-dialog';
import StorefrontIcon from '@mui/icons-material/Storefront';
import DoubleDatePicker from '../../../common/double-date-picker/double-date-picker';
import FilterBar from '../../../common/filter-bar/filter-bar';
import { isDesktop, isMobile, isTablet } from 'react-device-detect';
import CreateNewInvoice, { Target } from './create-new-orders-invoice';
import EditingInvoice from './editing-invoice';
import {
  DriverInvoiceState,
  DriverInvoiceType,
  IDriverInvoice,
} from '../../../../interfaces/common/driver-invoices';
import { OrderType } from '../../../../interfaces/delivery-company/orders';
import { DriverType } from '../../../../interfaces/delivery-company/drivers';
import AutocompleteWithThrottle from '../../../common/autocomplete-with-throttle/autocomplete-with-throttle';
import { DeliveryState, PaymentState } from '../../../../enums/orders';
import { showDriverInvoiceState } from '../../../../utils/driver-invoices';
import { PickupTaskState } from '../../../../enums/tasks';
import { IPickupTask } from '../../../../interfaces/delivery-company/tasks';
import ResponsiveInvoicesListing from '../../common/driver-invoices-listing';
import CustomDialog from '../../../common/custom-dialog/custom-dialog';
import { ErrorsFlattenner } from '../../../common/errors-flattenner/errors-flattenner';
import PendingCashOperation from './pending-cash-operation';
import { useSearchParams } from 'react-router-dom';
import { useSnackBar } from '../../../../contexts/snackbar';
import CreateNewDriverDeliveryInvoice from './driver-invoice-form/invoiceable-delivery-tasks';
import { DataResources, ResourceActionType, user_has_data_model_access } from '../../../../utils/permissions';
import { format } from 'date-fns';
import CreateNewDriverInvoice from './driver-invoice-form';
import { useInvoicePDFViewer } from '../../../../hooks/use-invoice-pdf-viewer';
import { PDFViewerDialog } from '../../../common/pdf-viewer-dialog/pdf-viewer-dialog';
import { useApiConfigWithBranch } from '../../../../hooks/use-api-config-with-branch';

export function DriverOrdersAccounting() {

  const [searchParams, setSearchParams] = useSearchParams();

  const {showMessage} = useSnackBar()
  const driverID = searchParams.get('driverID');
  const driverName = searchParams.get('driverName');
  const driverPhone = searchParams.get('driverPhone');
  const handleChange = (driver: DriverType | null) => {
    const newParams: any = driver
      ? {
          driverID: driver.id,
          driverName: driver.name,
          driverPhone: driver.phone_number,
        }
      : {};
    updateParams({ ...newParams, target: 'driver' });
  };

  const updateParams = (newParams: Record<string, string>) => {
    const updated = new URLSearchParams(searchParams.toString());

      Object.entries(newParams).forEach(([key, value]) => {
        updated.set(key, value);
      });

      setSearchParams(updated);
  };

  useEffect(()=>updateParams({target: 'driver'}),[])

  const [driver, setDriver] = useState<DriverType | null>(() => {
    return driverID && driverName && driverPhone
      ? ({
          id: driverID,
          name: driverName,
          phone_number: driverPhone,
        } as DriverType)
      : null;
  });

  const [filters, setFilters] = useState<IFilterOption[]>([]);
  const [invoicesList, setInvoicesList] = useState<IDriverInvoice[]>([]);
  const [invoicesCount, setInvoicesCount] = useState<number>(0);
  const [dateFilter, setDateFilter] = useState<DoubleDate>({
    startDate: null,
    endDate: null,
  });
  const [message, setMessage] = useState<string>('');
  const [response, setResponse] = useState<AxiosResponse>();
  const [isLoading, setIsLoading] = useState(false);
  const [lockedInvoiceMeta, setLockedInvoiceMeta] =
    useState<IDriverInvoice | null>(null);
  const [lockType, setLockType] = useState<'view' | 'delete' | null>(null);
  const [confirmUndoInvoice, setConfirmUndoInvoice] =
    useState<IDriverInvoice | null>(null);
  const [confirmPaymentFor, setConfirmPaymentFor] =
    useState<IDriverInvoice | null>(null);
  const [creatingNewInvoice, setCreatingNewInvoice] =
    useState<boolean>(false);

  const [pdfData, setPdfData] = useState<IDriverInvoice | null>(null);

  // Use the invoice PDF viewer hook
  const {
    pdfUrl,
    isLoading: isPdfLoading,
    showPdfViewer,
    pdfTitle,
    pdfFilename,
    fetchAndViewInvoice,
    closePdfViewer,
  } = useInvoicePDFViewer();

  const handleEditNote = async (invoice_pk: string) => {
    const note = prompt('Enter new note');
    if (note === null) {
      return;
    }

    const endpoint = `${API_ENDPOINT}/api/v1/driver-invoice/${invoice_pk}/`;

    try {
      const response = await axios.patch(endpoint, { notes: note }, config);
      const clonedRows: IDriverInvoice[] = invoicesList.map((invoice) =>
        invoice.id == invoice_pk ? { ...invoice, notes: note } : invoice
      );
      setInvoicesList(clonedRows);
      setResponse(response);
    } catch (e) {
      if (isAxiosError(e)) {
        setResponse(e.response);
        setMessage('Failed to modify invoice note: ' + e.response?.statusText);
      } else {
        setMessage('Failed to modify invoice note');
      }
    }
  };

  const { user } = useContext(AuthContext);
  const { config, branchKey } = useApiConfigWithBranch();

  const { paginationConfig, setPaginationConfig } =
    useContext(PaginationContext);
  const [paginationController, setPaginationController] = useState({
    page: 0,
    rowsPerPage: paginationConfig.rowsPerPage,
  });


  const createPickupInvoice = async (targets: Target[]) => {
    if (driver == null) {
      throw new Error('no driver was chosen');
    }
    const tasks = targets.map((target) => target as IPickupTask);
    return await axios.post(
      `${API_ENDPOINT}/api/v1/driver-invoices/`,
      {
        driver: driver.id,
        tasks_keys: tasks.map((task) => task.id),
        type: DriverInvoiceType.PICKUP,
      },
      config
    );
  };

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setPaginationController({
      ...paginationController,
      page: newPage,
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPaginationConfig({ ...paginationConfig, rowsPerPage: newRowsPerPage });
    setPaginationController({
      ...paginationController,
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
  };

  const resetPaginationController = () => {
    setPaginationController({
      ...paginationController,
      page: 0,
    });
  };

  const dateFilterAction = (dateFilterValue: DoubleDate) => {
    resetPaginationController();
    setDateFilter(dateFilterValue);
  };

  const confirmPayment = async (invoice: IDriverInvoice) => {
    setIsLoading(true);
    setResponse(undefined);
    try {
      const endpoint = `${API_ENDPOINT}/api/v1/driver-invoice/${invoice.id}/`;
      const response = await axios.patch(
        endpoint,
        { status: DriverInvoiceState.PAID },
        config
      );
      showMessage('Payment registered successfully', 'success');
      setResponse(response);
      setInvoicesList(
        invoicesList.map((item) =>
          item.id == invoice.id
            ? { ...item, status: DriverInvoiceState.PAID }
            : item
        )
      );
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setMessage('Failed to register payment: ' + e.response?.statusText);
        setResponse(e.response);
      } else {
        setMessage('Failed to register payment');
      }
    }
    setIsLoading(false);
  };

    const fetchInvoices = async () => {
      if (!user_has_data_model_access(user, DataResources.DRIVER_INVOICE, ResourceActionType.VIEW)) return;
      setIsLoading(true);

      let response: any;
      const relevant_fields = [
        'id',
        'driver',
        'type',
        'packages',
        'task_count',
        'total_amounts',
        'created_at',
        'status',
        'notes',
        'paid_at',
        'purpose',
      ];


      let endpoint = `${API_ENDPOINT}/api/v1/driver-invoices/?page=${paginationController.page + 1}&page_size=${paginationController.rowsPerPage}&fields=${relevant_fields.join(',')}`;

      const { startDate, endDate } = dateFilter;

      if (startDate !== null) {
        endpoint += `&date_from=${encodeURIComponent(startDate.format('YYYY-MM-DD'))}`;
      }

      if (endDate !== null) {
        endpoint += `&date_to=${encodeURIComponent(endDate.format('YYYY-MM-DD'))}`;
      }

      const filterQueryParams = buildQueryParams(filters);
      endpoint += `${filterQueryParams}`;

      if (driver != null) {
        endpoint += `&driver=${driver.id}`;
      }

      try {
        response = await axios.get<{
          count: number;
          results: IDriverInvoice[];
        }>(endpoint, config);
        console.log(response.data);

        setResponse(response);
        setInvoicesList(response.data.results);
        setInvoicesCount(response.data.count);
      } catch (e) {
        if (axios.isAxiosError(e)) {
          console.log('error: ', e);
          setResponse(e.response);
          setMessage('Failed to fetch invoices: ' + e.response?.statusText);
        } else {
          setMessage('Failed to fetch invoices');
        }
        console.log(e);
      }

      setIsLoading(false);
    };
    
  useEffect(() => {
    
  
    fetchInvoices();
  }, [dateFilter, paginationController, filters, driver, branchKey]);

  const fetchInvoiceContent = async (invoicemeta: IDriverInvoice) => {
    setLockedInvoiceMeta(invoicemeta);
    setLockType('view');

    try {
      // Use the hook to fetch and display invoice in PDF viewer
      await fetchAndViewInvoice(invoicemeta, 'driver');
      setResponse(undefined);
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
        setMessage(
          'Failed to load invoice content: ' + e.response?.statusText
        );
      } else {
        setMessage('Failed to load invoice content');
      }
    } finally {
      setLockedInvoiceMeta(null);
      setLockType(null);
    }
  }


  const legacyFetchInvoiceContent = async (invoicemeta: IDriverInvoice) => {
    setLockedInvoiceMeta(invoicemeta);
    setLockType('view');

    const endpoint = `${API_ENDPOINT}/api/v1/driver-invoice/${invoicemeta.id}/`;

    try {
      const response = await axios.get(endpoint, config);
      setResponse(response);
      console.log('target = ', response);
      const data: IDriverInvoice = response.data;
      setPdfData(data);
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
        setMessage(
          'Failed to download invoice content: ' + e.response?.statusText
        );
      } else {
        setMessage('Failed to download invoice content');
      }
    }
    setLockedInvoiceMeta(null);
    setLockType(null);
  };

  const filterQueryAction = (filters: IFilterOption[]) => {
    resetPaginationController();
    setFilters(filters);
  };

  const handleUndoInvoice = async (invoicemeta: IDriverInvoice) => {
    const endpoint = `${API_ENDPOINT}/api/v1/driver-invoice/${invoicemeta.id}/`;
    try {
      await axios.delete(endpoint, config);
      setInvoicesList(
        invoicesList.filter((invoice) => invoice.id !== invoicemeta.id)
      );
      setInvoicesCount(invoicesCount - 1);
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
        setMessage('Failed to undo invoice: ' + e.response?.statusText);
      } else {
        setMessage('Failed to undo invoice');
      }
    }

    setLockType(null);
    setLockedInvoiceMeta(null);
  };

  const handleFetchDrivers = async (query: string): Promise<DriverType[]> => {
    const endpoint = `${API_ENDPOINT}/api/v1/drivers/?page_size=10&name_or_phone=${encodeURIComponent(query)}`;

    try {
      const response = await axios.get<{
        count: number;
        results: DriverType[];
      }>(endpoint, config);

      return response.data.results;
    } catch (error) {
      console.log(error);
    }

    return [];
  };

  console.log('pedning: ', confirmPaymentFor);

  return (
    <div className="content-container">
      <Box>
        {message !== '' && (
          <>
            <Alert
              severity={
                response && response.status >= 200 && response.status < 300
                  ? 'success'
                  : 'error'
              }
            >
              <>
                {message}

                {response && response.status >= 300 && (
                  <ErrorsFlattenner rawErrors={response.data} />
                )}
              </>
            </Alert>

            <br />
          </>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
            gap: 4,
          }}
        >
          <Button
            disabled={driver == null || !user_has_data_model_access(user, DataResources.DRIVER_INVOICE, ResourceActionType.CREATE)}
            variant="contained"
            size={(isMobile && !isTablet) ? 'small' : 'large'}
            onClick={() => setCreatingNewInvoice(true)}
          >
            New Invoice
          </Button>
        </div>
        <br />

        {driver && (
          <>
            <PendingCashOperation driver_id={driver.id} /> <br />
          </>
        )}
        <AutocompleteWithThrottle
          label={'Select Driver'}
          fetchAllOnLoad
          required={false}
          fetch={handleFetchDrivers}
          value={driver}
          getOptionLabel={(option: DriverType) =>
            option.name + ' ' + option.phone_number
          }
          onChange={(e, value) => {
            setDriver(value);
            handleChange(value);
          }}
        />
        <br />

        <>
          {
            !user_has_data_model_access(user, DataResources.DRIVER_INVOICE, ResourceActionType.VIEW) ? 
              <Alert severity='warning'>You do not have permission to view invoices.</Alert> : 
              <>
                <Accordion elevation={0}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                  >
                    <Typography
                      variant="h5"
                      component="h5"
                      sx={{ textAlign: 'center', mt: 1, mb: 1 }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 5,
                        }}
                      >
                        <ManageSearchIcon fontSize="large" color="primary" />
                        <span>
                          <b>Search and Filter Controls</b>
                        </span>
                      </div>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <DoubleDatePicker dateFilterAction={dateFilterAction} />
                    <br />
                    <FilterBar
                      filterQueryAction={filterQueryAction}
                      currentlySelectedFilters={filters}
                      filterKeys={['driver invoice status', 'driver invoice type']}
                    />
                  </AccordionDetails>
                </Accordion>

                <ResponsiveInvoicesListing
                  invoicesList={invoicesList}
                  isLoading={isLoading}
                  target={'driver'}
                  setLockedInvoiceMeta={setLockedInvoiceMeta}
                  setLockType={setLockType}
                  setConfirmUndoInvoice={setConfirmUndoInvoice}
                  setConfirmPaymentFor={setConfirmPaymentFor}
                  handleEditNote={handleEditNote}
                  fetchInvoiceContent={fetchInvoiceContent}
                  legacyFetchInvoiceContent={legacyFetchInvoiceContent}
                  lockedInvoiceMeta={lockedInvoiceMeta}
                  lockType={lockType}
                />

                <TablePagination
                  component="div"
                  onPageChange={handlePageChange}
                  page={paginationController.page}
                  count={invoicesCount}
                  rowsPerPage={paginationController.rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50, 100, 200, 250, 500, 1000]}
                  labelRowsPerPage=""
                  style={{ overflow: 'visible' }}
                />

              </>
          }
        
        </>
        
      </Box>

      {pdfData && pdfData.driver && (
        <InvoicePDFDialog
          pdfData={pdfData}
          onClose={() => setPdfData(null)}
          driver={pdfData.driver}
        />
      )}

      {driver != null && (
        <CustomDialog
          open={creatingNewInvoice}
          fullWidth
          maxWidth="xl"
          fullScreen={(isMobile && !isTablet)}
          onClose={() => {
            setCreatingNewInvoice(false);
          }}
        >
          <div
            style={{
              textAlign: 'right',
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <IconButton
              onClick={() => {
                setCreatingNewInvoice(false);
              }}
            >
              <ClearIcon color="primary" fontSize="large" />
            </IconButton>
          </div>

          <DialogContent sx={{ overflow: 'visible', minHeight: 400 }}>
            <CreateNewDriverInvoice 
              driver={driver} 
              onSuccess={()=>{
                  showMessage("Invoice created successfully!")
                  fetchInvoices()
                  setCreatingNewInvoice(false);
                }}
            />
          </DialogContent>
        </CustomDialog>
      )}


      {confirmPaymentFor != null && (
        <ConfirmAction
          message={`Please confirm payment operation for driver ${confirmPaymentFor?.driver?.name}`}
          action={() => confirmPayment(confirmPaymentFor)}
          isSevereAction
          confirmSevereActionTarget="pay"
          isOpen={confirmPaymentFor != null}
          handleClose={() => setConfirmPaymentFor(null)}
        >
          <Alert severity="warning">This operation cannot be undone.</Alert>
        </ConfirmAction>
      )}

      {confirmUndoInvoice && (
        <ConfirmAction
          message={'Are you sure you want to undo this invoice?'}
          action={() => handleUndoInvoice(confirmUndoInvoice)}
          isOpen={confirmUndoInvoice != null}
          handleClose={() => {
            setConfirmUndoInvoice(null);
            setLockType(null);
            setLockedInvoiceMeta(null);
          }}
          isSevereAction={true}
          confirmSevereActionTarget="undo invoice"
        >
          <div>
            Attention: This action will change the payment state of the orders
            included in the invoice
          </div>
        </ConfirmAction>
      )}

      {/* PDF Viewer Dialog */}
      <PDFViewerDialog
        open={showPdfViewer}
        onClose={closePdfViewer}
        pdfUrl={pdfUrl}
        title={pdfTitle}
        filename={pdfFilename}
      />
    </div>
  );
}

export default DriverOrdersAccounting;
